import React from 'react';
import { useMediaQuery, useReducedMotion } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconAlert } from '../../assets/images/icons/icon_alert.svg';
import { ReactComponent as IconBell } from '../../assets/images/icons/icon_bell.svg';
import { ReactComponent as IconLocation } from '../../assets/images/icons/icon_location.svg';
import { ReactComponent as IconSatellite } from '../../assets/images/icons/icon_satellite.svg';
import { ReactComponent as IconVibration } from '../../assets/images/icons/icon_vibration.svg';
import { Species } from '../../types/gql-op-types';
import { PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import styles from './HardwareFirstGpsBand.module.scss';

type Item = { Icon: React.FC<React.SVGProps<SVGSVGElement>>; key: string };

const DOG_ITEMS: Item[] = [
  { Icon: IconLocation, key: 'pdp_v2_gps_safe_zones' },
  { Icon: IconBell, key: 'pdp_v2_gps_escape_alerts' },
  { Icon: IconAlert, key: 'pdp_v2_gps_lost_dog_mode' },
];
const CAT_ITEMS: Item[] = [DOG_ITEMS[0], DOG_ITEMS[1], { Icon: IconAlert, key: 'pdp_v2_gps_lost_pet_mode' }];
const ULTRA_ITEMS: Item[] = [
  { Icon: IconSatellite, key: 'pdp_v2_gps_remote_coverage' },
  { Icon: IconBell, key: 'pdp_v2_gps_escape_alerts' },
  { Icon: IconVibration, key: 'pdp_v2_gps_callback' },
];

/**
 * The first below-fold section on the solo page: the tracking pitch over a looping map, the copy on
 * its own light so it stays readable where the map runs under it. ultra sells the satellite handoff
 * over its own map, the cats page shows a cat on its map, everything else sells live gps.
 */
export default function HardwareFirstGpsBand({
  deviceKey,
  species,
}: {
  deviceKey: PickYourFiDeviceKey;
  species: Species;
}) {
  const { t } = useTranslation();
  const ultra = deviceKey === 'ultra';
  const items = ultra ? ULTRA_ITEMS : species === Species.CAT ? CAT_ITEMS : DOG_ITEMS;
  // one source per layout, so a phone never downloads the desktop loop
  // matches the stylesheet's $band-wide, where the band takes the wide map
  const wide = useMediaQuery('(min-width: 700px)');
  const reduceMotion = useReducedMotion();
  // ultra walks its own trail; the cats page pins a cat on the same map the dog trackers use
  const map = ultra ? 'ultra' : species === Species.CAT ? 'cat' : 'mini';
  const src = `/product_images/hardware-first/gps/${map}-${wide ? 'desktop' : 'phone'}.mp4`;
  return (
    <section className={styles.band}>
      <video
        key={src}
        className={styles.map}
        src={src}
        autoPlay={!reduceMotion}
        loop
        muted
        playsInline
        aria-hidden="true"
      />
      <div className={styles.scrim} />
      <div className={styles.copy}>
        <div className={styles.eyebrow}>{t(ultra ? 'pdp_v2_gps_ultra_eyebrow' : 'pdp_v2_gps_eyebrow')}</div>
        <h2 className={styles.title}>{t(ultra ? 'pdp_v2_gps_ultra_title' : 'pdp_v2_gps_title')}</h2>
        <p className={styles.sub}>{t(ultra ? 'pdp_v2_gps_ultra_sub' : 'pdp_v2_gps_sub')}</p>
        <ul className={styles.list}>
          {items.map(({ Icon, key }) => (
            <li key={key} className={styles.item}>
              <span className={styles.itemIcon}>
                <Icon />
              </span>
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
        {ultra && <div className={styles.footnote}>{t('pdp_v2_gps_satellite_footnote')}</div>}
      </div>
    </section>
  );
}
